import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, OnDestroy } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { NavigationEnd, Router } from '@angular/router';
import { RouteStateService } from 'app/core/services/route-state.service';
import { SettingsService } from 'app/core/services/settings.service';
import { ToggleButtonComponent } from '@shared/components/ui/toggle-button/toggle-button.component';
import { ToggleButtonOptionDirective } from '@shared/components/ui/toggle-button/toggle-button-option.directive';
import { TenantUrlUtilService } from '@shared/services/tenant-url-util.service';
import { environment } from '@env/environment';
import { filter, Subject, takeUntil } from 'rxjs';

type Tab =
  | {
      icon: string;
      label: string;
      external: true;
      url: string;
    }
  | {
      icon: string;
      label: string;
      external?: false;
      base: 'orders' | 'messaging' | 'services' | 'invoices';
      build: () => {
        commands: any[];
        extras?: Parameters<Router['navigate']>[1];
        useNavigateByUrl?: boolean;
      };
    };

@Component({
  selector: 'app-module-tabs',
  standalone: true,
  imports: [
    CommonModule,
    MatIconModule,
    ToggleButtonComponent,
    ToggleButtonOptionDirective,
  ],
  templateUrl: './module-tabs.component.html',
  styleUrls: ['./module-tabs.component.scss'],
})
export class ModuleTabsComponent implements OnInit, OnDestroy {
  private readonly routeState = inject(RouteStateService);
  private readonly router = inject(Router);
  private readonly settingsService = inject(SettingsService);
  private readonly tenantUrlUtil = inject(TenantUrlUtilService);
  private readonly destroy$ = new Subject<void>();

  tabs: Tab[] = [
    {
      icon: 'shopping_bag',
      label: 'Pedidos',
      base: 'orders',
      build: () => {
        const lastRoute = this.settingsService.get<string>('lastOrdersRoute');
        const tenantId = this.routeState.getRouteParam('tenantId');

        if (lastRoute && this.canApplyLastRoute(lastRoute)) {
          const commands = [lastRoute];
          this.clearLastOrdersRoute();
          return { commands, useNavigateByUrl: true };
        }

        const commands = tenantId ? ['/orders', tenantId] : ['/orders'];
        return { commands, extras: { queryParamsHandling: 'preserve' } };
      },
    },
    {
      icon: 'message',
      label: 'Comunicaciones',
      base: 'messaging',
      build: () => {
        const tenantId = this.routeState.getRouteParam('tenantId');
        const commands = tenantId ? ['/messaging', tenantId] : ['/messaging'];
        return { commands, extras: { queryParamsHandling: 'preserve' } };
      },
    },
    {
      icon: 'build',
      label: 'Servicios',
      base: 'services',
      build: () => {
        const tenantId = this.routeState.getRouteParam('tenantId');
        const commands = tenantId ? ['/services', tenantId] : ['/services'];
        return { commands, extras: { queryParamsHandling: 'preserve' } };
      },
    },
    {
      icon: 'receipt',
      label: 'Comprobantes',
      base: 'invoices',
      build: () => {
        const tenantId = this.routeState.getRouteParam('tenantId');
        const commands = tenantId ? ['/invoices', tenantId] : ['/invoices'];
        return { commands, extras: { queryParamsHandling: 'preserve' } };
      },
    },
  ];

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd
        ),
        takeUntil(this.destroy$)
      )
      .subscribe((event) => {
        const fullUrl = event.urlAfterRedirects;
        const urlWithoutParams = fullUrl.split('?')[0];

        if (
          urlWithoutParams.includes('/orders/') &&
          !this.isOrdersListRoute(urlWithoutParams)
        ) {
          this.saveOrdersRoute(fullUrl);
        }
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private isOrdersListRoute(url: string): boolean {
    const segments = url.split('/').filter((s) => s);
    return segments.length === 2 && segments[0] === 'orders';
  }

  getTogglePaths(): Record<string, string> {
    const paths: Record<string, string> = {};
    const tenantId = this.routeState.getRouteParam('tenantId');

    this.tabs.forEach((tab) => {
      if ('base' in tab && !tab.external) {
        const commands = tenantId
          ? [`/${tab.base}`, tenantId]
          : [`/${tab.base}`];
        paths[tab.base] = commands.join('/');
      }
    });

    return paths;
  }

  getNavigationCallbacks(): Record<string, () => void> {
    const callbacks: Record<string, () => void> = {};

    this.tabs.forEach((tab) => {
      if ('base' in tab && !tab.external) {
        callbacks[tab.base] = () => {
          const { commands, extras, useNavigateByUrl } = tab.build();

          if (useNavigateByUrl) {
            this.router.navigateByUrl(commands[0]);
          } else {
            this.router.navigate(commands, extras);
          }
        };
      }
    });

    return callbacks;
  }

  private saveOrdersRoute(url: string): void {
    this.settingsService.set('lastOrdersRoute', url);
  }

  private clearLastOrdersRoute(): void {
    this.settingsService.remove('lastOrdersRoute');
  }

  private canApplyLastRoute(lastRoute: string): boolean {
    const urlTree = this.router.parseUrl(lastRoute);
    const queryParams = urlTree.queryParams;

    const searchValue = queryParams[environment.nameSearchValueParamUrl];
    
    if (!searchValue) {
      return false;
    }

    const documentType = this.tenantUrlUtil.getDocumentType(searchValue);
    
    return documentType === 'ORDER_ID';
  }
}
